import { useEffect, useMemo, useState } from "react";
import { FaCircleUser } from "react-icons/fa6";
import { apiFetch } from "../lib/api";
import { toast } from "react-toastify";

type PersonalInfo = {
  id: number;
  sender_name?: string;
  email?: string;
  pan?: string;
  gstin?: string;
  tag?: string;
  tag_color?: string;
  header_name?: string;
  header_tagline?: string;
  footer_text?: string;
  signature_path?: string;
  signature_url?: string;
  gst_rate?: number;
};

const tagColors = ["#0f766e", "#2563eb", "#7c3aed", "#dc2626", "#f59e0b"];
const defaultTagColor = tagColors[0];

export default function PersonalInfoPage() {
  const [personalInfos, setPersonalInfos] = useState<PersonalInfo[]>([]);
  const [form, setForm] = useState({
    id: null as number | null,
    senderName: "",
    email: "",
    pan: "",
    gstin: "",
    tag: "",
    tagColor: defaultTagColor,
    headerName: "",
    headerTagline: "",
    footerText: "",
    signaturePath: "",
    signatureUrl: "",
    gstRate: "18",
  });
  const [status, setStatus] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string>("");
  const [isUploadingSignature, setIsUploadingSignature] = useState(false);

  const loadPersonalInfos = () => {
    apiFetch("/api/personal-info/all")
      .then(async (res) => {
        if (res.ok) {
          const data = await res.json();
          setPersonalInfos(Array.isArray(data) ? data : []);
          return;
        }
        throw new Error("all endpoint unavailable");
      })
      .catch(() =>
        apiFetch("/api/personal-info")
          .then((res) => res.json())
          .then((data) => setPersonalInfos(data ? [data] : []))
          .catch(() => setPersonalInfos([]))
      );
  };

  useEffect(() => {
    loadPersonalInfos();
  }, []);

  const filteredPersonalInfos = useMemo(() => {
    const term = searchText.trim().toLowerCase();
    if (!term) {
      return personalInfos;
    }

    return personalInfos.filter((info) => {
      const pan = info.pan?.toLowerCase() || "";
      const gstin = info.gstin?.toLowerCase() || "";
      const senderName = info.sender_name?.toLowerCase() || "";
      const email = info.email?.toLowerCase() || "";
      const headerName = info.header_name?.toLowerCase() || "";
      const tag = info.tag?.toLowerCase() || "";
      const headerTagline = info.header_tagline?.toLowerCase() || "";
      const footerText = info.footer_text?.toLowerCase() || "";
      const gstRate =
        info.gst_rate !== undefined && info.gst_rate !== null
          ? String(info.gst_rate).toLowerCase()
          : "";

      return (
        senderName.includes(term) ||
        email.includes(term) ||
        pan.includes(term) ||
        gstin.includes(term) ||
        tag.includes(term) ||
        headerName.includes(term) ||
        headerTagline.includes(term) ||
        footerText.includes(term) ||
        gstRate.includes(term)
      );
    });
  }, [personalInfos, searchText]);

  const handleChange = (field: keyof typeof form, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("Saving personal info...");

    try {
      const response = await apiFetch(
        form.id ? `/api/personal-info/${form.id}` : "/api/personal-info",
        {
          method: form.id ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pan: form.pan,
            gstin: form.gstin,
            tag: form.tag,
            tagColor: form.tagColor,
            senderName: form.senderName,
            email: form.email,
            headerName: form.headerName,
            headerTagline: form.headerTagline,
            footerText: form.footerText,
            signaturePath: form.signaturePath || null,
            gstRate: form.gstRate ? Number(form.gstRate) : null,
          }),
        }
      );

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to save");
      }

      setForm({
        id: null,
        senderName: "",
        email: "",
        pan: "",
        gstin: "",
        tag: "",
        tagColor: defaultTagColor,
        headerName: "",
        headerTagline: "",
        footerText: "",
        signaturePath: "",
        signatureUrl: "",
        gstRate: "18",
      });
      const message = form.id ? "Personal info updated." : "Personal info saved.";
      setStatus(message);
      toast.success(message);
      loadPersonalInfos();
    } catch (error: any) {
      setStatus(error?.message || "Failed to save personal info.");
      toast.error(error?.message || "Failed to save personal info.");
    }
  };

  const handleEdit = (info: PersonalInfo) => {
    setForm({
      id: info.id,
      senderName: info.sender_name || "",
      email: info.email || "",
      pan: info.pan || "",
      gstin: info.gstin || "",
      tag: info.tag || "",
      tagColor: info.tag_color || defaultTagColor,
      headerName: info.header_name || "",
      headerTagline: info.header_tagline || "",
      footerText: info.footer_text || "",
      signaturePath: info.signature_path || "",
      signatureUrl: info.signature_url || "",
      gstRate:
        info.gst_rate !== undefined && info.gst_rate !== null
          ? String(info.gst_rate)
          : "18",
    });
    setStatus(null);
  };

  const handleDuplicate = (info: PersonalInfo) => {
    setForm({
      id: null,
      senderName: info.sender_name || "",
      email: info.email || "",
      pan: info.pan || "",
      gstin: info.gstin || "",
      tag: info.tag || "",
      tagColor: info.tag_color || defaultTagColor,
      headerName: info.header_name || "",
      headerTagline: info.header_tagline || "",
      footerText: info.footer_text || "",
      signaturePath: info.signature_path || "",
      signatureUrl: info.signature_url || "",
      gstRate:
        info.gst_rate !== undefined && info.gst_rate !== null
          ? String(info.gst_rate)
          : "18",
    });
    setStatus("Personal info copied. Save to create a new entry.");
    toast.info("Personal info copied. Save to create a new entry.");
  };

  const handleReset = () => {
    setForm({
      id: null,
      senderName: "",
      email: "",
      pan: "",
      gstin: "",
      tag: "",
      tagColor: defaultTagColor,
      headerName: "",
      headerTagline: "",
      footerText: "",
      signaturePath: "",
      signatureUrl: "",
      gstRate: "18",
    });
    setStatus(null);
  };

  const readFileAsDataUrl = (file: File) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          resolve(reader.result);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });

  const handleSignatureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    if (file.type !== "image/png" && !file.name.toLowerCase().endsWith(".png")) {
      setStatus("Please upload a PNG signature file.");
      toast.error("Please upload a PNG signature file.");
      event.target.value = "";
      return;
    }

    setIsUploadingSignature(true);
    setStatus("Uploading signature...");
    try {
      const dataUrl = await readFileAsDataUrl(file);
      const response = await apiFetch("/api/personal-info/signature", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fileName: file.name,
          dataUrl,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to upload signature");
      }
      setForm((prev) => ({
        ...prev,
        signaturePath: String(data?.signaturePath || ""),
        signatureUrl: String(data?.signatureUrl || ""),
      }));
      setStatus("Signature uploaded.");
      toast.success("Signature uploaded.");
    } catch (error: any) {
      setStatus(error?.message || "Failed to upload signature.");
      toast.error(error?.message || "Failed to upload signature.");
    } finally {
      setIsUploadingSignature(false);
      event.target.value = "";
    }
  };

  return (
    <section className="grid gap-6">
      <div className="card p-6">
        <h2 className="font-display text-xl font-semibold text-ink">Sender Profiles</h2>
        <p className="text-sm text-slate-500">
          Manage your PAN and GST details used in invoices.
        </p>
      </div>

      <div className="grid items-start gap-6 md:grid-cols-[1.1fr_1fr]">
        <form className="card grid gap-4 p-6" onSubmit={handleSubmit}>
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-600">
              {form.id ? "Edit Personal Info" : "Add Personal Info"}
            </h3>
            {form.id && (
              <button type="button" className="button-secondary" onClick={handleReset}>
                New Entry
              </button>
            )}
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-600">TAG</label>
            <div className="mt-1 grid gap-3 rounded-xl border border-slate-200 bg-white p-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <div className="flex items-center gap-3">
                <FaCircleUser
                  className="h-5 w-5 flex-none"
                  style={{ color: form.tagColor }}
                  aria-hidden="true"
                />
                <input
                  className="input"
                  placeholder="Example: Personal, Business, Export..."
                  value={form.tag}
                  onChange={(e) => handleChange("tag", e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                {tagColors.map((color) => (
                  <button
                    key={color}
                    type="button"
                    className={`h-7 w-7 rounded-full border-2 transition ${
                      form.tagColor === color
                        ? "border-slate-800"
                        : "border-white shadow ring-1 ring-slate-200"
                    }`}
                    style={{ backgroundColor: color }}
                    aria-label={`Use tag color ${color}`}
                    onClick={() => handleChange("tagColor", color)}
                  />
                ))}
                <input
                  type="color"
                  className="h-8 w-10 cursor-pointer rounded border border-slate-200 bg-white p-1"
                  value={form.tagColor}
                  aria-label="Custom tag color"
                  onChange={(e) => handleChange("tagColor", e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-sm font-semibold text-slate-600">Sender Name (Optional)</label>
              <input
                className="input"
                value={form.senderName}
                onChange={(e) => handleChange("senderName", e.target.value)}
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">Email</label>
              <input
                className="input"
                type="email"
                value={form.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="billing@yourcompany.com"
                required
              />
              <p className="mt-1 text-xs text-slate-400">
                Invoice email replies will be sent here.
              </p>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-600">PAN</label>
              <input
                className="input"
                value={form.pan}
                onChange={(e) => handleChange("pan", e.target.value)}
              />
            </div>
            <div className="md:col-start-2">
              <label className="text-sm font-semibold text-slate-600">GSTIN</label>
              <input
                className="input"
                value={form.gstin}
                onChange={(e) => handleChange("gstin", e.target.value)}
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-600">Invoice Header</label>
            <input
              className="input"
              value={form.headerName}
              onChange={(e) => handleChange("headerName", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-600">Invoice Tagline</label>
            <textarea
              className="input min-h-[90px]"
              value={form.headerTagline}
              onChange={(e) => handleChange("headerTagline", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-600">GST Rate (%)</label>
            <input
              className="input"
              type="number"
              min="0"
              step="0.01"
              value={form.gstRate}
              onChange={(e) => handleChange("gstRate", e.target.value)}
            />
          </div>

          <div>
            <label className="text-sm font-semibold text-slate-600">Invoice Footer</label>
            <textarea
              className="input min-h-[90px]"
              value={form.footerText}
              onChange={(e) => handleChange("footerText", e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <label className="text-sm font-semibold text-slate-600">Signature (PNG)</label>
            <input
              className="input"
              type="file"
              accept="image/png,.png"
              onChange={handleSignatureUpload}
              disabled={isUploadingSignature}
            />
            {form.signaturePath && (
              <div className="grid gap-2">
                <img
                  src={form.signatureUrl}
                  alt="Signature preview"
                  className="h-20 w-auto rounded border border-slate-200 bg-white p-2 object-contain"
                />
                <button
                  type="button"
                  className="button-secondary w-fit"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, signaturePath: "", signatureUrl: "" }))
                  }
                >
                  Remove Signature
                </button>
              </div>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button className="button" type="submit">
              {form.id ? "Update Personal Info" : "Save Personal Info"}
            </button>
            {form.id && (
              <button type="button" className="button-secondary" onClick={handleReset}>
                Cancel Edit
              </button>
            )}
            {status && <span className="text-sm text-slate-500">{status}</span>}
          </div>
        </form>

        <div className="card p-0 max-h-[calc(100vh-220px)] overflow-hidden lg:self-start">
          <div className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 p-6 backdrop-blur">
            <h3 className="text-sm font-semibold text-slate-600">Saved Info</h3>
            <div className="mt-3 grid gap-2">
              <label className="text-xs font-semibold text-slate-500">Search saved info</label>
              <input
                className="input"
                placeholder="Search PAN/GSTIN/name/text..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
              {personalInfos.length > 0 &&
                filteredPersonalInfos.length === 0 &&
                searchText.trim() !== "" && (
                  <p className="text-xs text-slate-500">
                    No personal info matches "{searchText.trim()}".
                  </p>
                )}
            </div>
          </div>

          <div className="max-h-[calc(100vh-220px-140px)] overflow-auto p-6">
            {filteredPersonalInfos.length === 0 ? (
              <p className="text-sm text-slate-500">
                {personalInfos.length === 0
                  ? "No personal info saved yet."
                  : "No personal info matches this search."}
              </p>
            ) : (
              <ul className="grid gap-3">
                {filteredPersonalInfos.map((info) => (
                  <li
                    key={info.id}
                    className="rounded-xl border border-slate-200 bg-white p-4"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid gap-1 text-sm text-slate-600">
                        {info.tag && (
                          <p className="mb-1 inline-flex w-fit items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-700">
                            <FaCircleUser
                              className="h-3.5 w-3.5"
                              style={{ color: info.tag_color || defaultTagColor }}
                              aria-hidden="true"
                            />
                            {info.tag}
                          </p>
                        )}
                        {info.sender_name && (
                          <p>
                            <span className="font-semibold">Sender Name:</span> {info.sender_name}
                          </p>
                        )}
                        {info.email && (
                          <p>
                            <span className="font-semibold">Email:</span> {info.email}
                          </p>
                        )}
                        {info.pan && (
                          <p>
                            <span className="font-semibold">PAN:</span> {info.pan}
                          </p>
                        )}
                        {info.gstin && (
                          <p>
                            <span className="font-semibold">GSTIN:</span> {info.gstin}
                          </p>
                        )}
                        {info.header_name && (
                          <p>
                            <span className="font-semibold">Invoice Header:</span> {info.header_name}
                          </p>
                        )}
                        {info.header_tagline && (
                          <p>
                            <span className="font-semibold">Invoice Tagline:</span> {info.header_tagline}
                          </p>
                        )}
                        {info.gst_rate !== undefined && info.gst_rate !== null && (
                          <p>
                            <span className="font-semibold">GST Rate:</span> {info.gst_rate}%
                          </p>
                        )}
                        {info.footer_text && (
                          <p>
                            <span className="font-semibold">Invoice Footer:</span> {info.footer_text}
                          </p>
                        )}
                        {info.signature_path && info.signature_url && (
                          <div className="grid gap-1">
                            <span className="font-semibold">Signature:</span>
                            <img
                              src={info.signature_url}
                              alt="Saved signature"
                              className="h-16 w-auto rounded border border-slate-200 bg-white p-1 object-contain"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-col gap-2">
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => handleEdit(info)}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="button-secondary"
                          onClick={() => handleDuplicate(info)}
                        >
                          Duplicate
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
